<template>
  <div>
    <v-card class="pa-4">
      <v-form @submit.prevent="getActiveGiftCard">
        <div class="d-flex align-center" style="gap: 12px">
          <v-text-field
            v-model.trim="cardCode"
            class="font-weight-bold"
            label="Card Code"
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            clearable
            autofocus
            hide-details></v-text-field>
          <v-btn type="submit" color="accent" height="40" :loading="loading" :disabled="!cardCode">Search</v-btn>
        </div>
      </v-form>
    </v-card>

    <!-- Nothing searched yet, or the last search failed -->
    <v-card v-if="!giftCard" class="mt-6 px-6 py-10 text-center">
      <v-avatar color="grey lighten-3" size="56" class="mb-4">
        <v-icon color="grey darken-1">mdi-wallet-giftcard</v-icon>
      </v-avatar>
      <div class="text-h6 font-weight-bold">Check a gift card</div>
      <div class="text-body-2 grey--text text--darken-1 mt-2 mx-auto" style="max-width: 480px">
        Enter a card code to see its balance, status and details.
      </div>
    </v-card>

    <template v-else>
      <v-card class="mt-6 pa-4">
        <div class="d-flex align-center flex-wrap" style="gap: 12px">
          <div>
            <div class="d-flex align-center flex-wrap" style="gap: 8px">
              <span class="text-h6 font-weight-bold">{{ humanize(giftCard.type) }}</span>
              <v-chip small :color="statusChip.color" :text-color="statusChip.text" class="font-weight-bold">
                {{ humanize(giftCard.status) }}
              </v-chip>
              <v-chip
                v-if="isFullyUsed"
                small
                :color="defaultChip.color"
                :text-color="defaultChip.text"
                class="font-weight-bold">
                Fully Used
              </v-chip>
            </div>
            <div class="d-flex align-center mt-1">
              <span class="grey--text text--darken-1 mr-2">Code:</span>
              <span class="font-weight-bold">{{ giftCard.code }}</span>
              <v-tooltip top :open-delay="10" :close-delay="5">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="mango-red"
                    small
                    class="ml-2"
                    v-bind="attrs"
                    v-on="on"
                    @click="copyToClipboard(giftCard.code)">
                    mdi-content-copy
                  </v-icon>
                </template>
                <span>Copy</span>
              </v-tooltip>
            </div>
          </div>
          <v-spacer></v-spacer>
          <GiftCardPdf
            :disabled="giftCard.status !== 'completed'"
            tooltip-message="PDF access is not allowed for gift cards that are not completed."
            :giftCardCode="giftCard.code" />
        </div>
      </v-card>

      <v-row class="mt-1">
        <v-col v-for="tile in summaryTiles" :key="tile.label" cols="12" sm="4">
          <v-card class="fill-height px-4 py-3">
            <div class="text-subtitle-2 grey--text text--darken-1">{{ tile.label }}</div>
            <div class="text-h5 font-weight-bold my-1">{{ tile.value }}</div>
            <div v-if="tile.caption" class="text-caption grey--text text--darken-1">{{ tile.caption }}</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Side by side on large screens, so the whole card fits without scrolling -->
      <v-row>
        <v-col cols="12" md="6" lg="4">
          <v-card class="fill-height pa-4">
            <div class="text-h6 font-weight-bold mb-2">Details</div>
            <div class="detail-row">
              <span class="detail-label">Code</span>
              <span>{{ giftCard.code }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Status</span>
              <span>{{ humanize(giftCard.status) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Type</span>
              <span>{{ humanize(giftCard.type) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Description</span>
              <span class="pre-line">{{ giftCard.internalDescription || "—" }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Referenced Orders</span>
              <span>{{ giftCard.internalReferencedOrderNumbers || "—" }}</span>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="6" lg="4">
          <v-card class="fill-height pa-4">
            <div class="text-h6 font-weight-bold mb-2">History</div>
            <div v-for="row in historyDates" :key="row.label" class="detail-row">
              <span class="detail-label">{{ row.label }}</span>
              <dateTimeField v-if="row.date" :date="row.date" type="dateTime"></dateTimeField>
              <span v-else>—</span>
            </div>
            <div v-for="row in historyUsers" :key="row.label" class="detail-row">
              <span class="detail-label">{{ row.label }}</span>
              <div v-if="row.user">
                <div>{{ row.user.fullName ?? row.user.name }}</div>
                <div v-if="row.user.email" class="text-caption grey--text text--darken-1">{{ row.user.email }}</div>
              </div>
              <span v-else>—</span>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="6" lg="4">
          <v-card class="fill-height pa-4">
            <div class="text-h6 font-weight-bold mb-2">Card Content</div>
            <div class="detail-row">
              <span class="detail-label">Title Line 1</span>
              <span>{{ giftCard.title1 || "—" }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Title Line 2</span>
              <span>{{ giftCard.title2 || "—" }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Message</span>
              <span class="pre-line">{{ giftCard.message || "—" }}</span>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
import common from "@/mixins/common";
import { getErrorMessage } from "@/util/helpers";
import TrainplanetApi from "@/util/trainplanet.api";
import GiftCardPdf from "@/components/order/giftCard/GiftCardPdf.vue";
import dateTimeField from "@/components/common/dateTimeField.vue";

// Light chips like the promo code chip in the order items (lighten-5 background, darken-4 bold text),
// in the hues of the status toolbars on the Compensation and Staff Cards pages.
// Grey uses lighten-3, as grey lighten-5 barely shows on the white card.
const STATUS_CHIPS = {
  pending: { color: "orange lighten-5", text: "orange darken-4" },
  completed: { color: "green lighten-5", text: "green darken-4" },
  cancelled: { color: "red lighten-5", text: "red darken-4" },
};
const DEFAULT_CHIP = { color: "grey lighten-3", text: "grey darken-4" };

export default {
  components: {
    GiftCardPdf,
    dateTimeField,
  },
  mixins: [common],
  props: {
    code: String, // from the link query, searched on load
  },
  data: () => ({
    cardCode: "",
    giftCard: null,
    loading: false,
  }),
  computed: {
    statusChip() {
      return STATUS_CHIPS[this.giftCard.status] ?? DEFAULT_CHIP;
    },
    defaultChip: () => DEFAULT_CHIP,
    isFullyUsed() {
      return typeof this.giftCard.remainingAmount === "number" && this.giftCard.remainingAmount <= 0;
    },
    summaryTiles() {
      const { amount, remainingAmount } = this.giftCard;
      let usage;
      // Without both amounts the percentage and the used amount would be made up
      if (typeof amount === "number" && typeof remainingAmount === "number") {
        const remainingPercent =
          amount > 0 ? Math.min(100, Math.max(0, Math.round((remainingAmount / amount) * 100))) : 0;
        const usedAmount = Math.round((amount - remainingAmount) * 100) / 100;
        usage = `${remainingPercent}% left · ${this.formatAmount(usedAmount)} used`;
      }

      return [
        {
          label: "Amount",
          value: this.formatAmount(amount),
          caption: "Value of the card when it was issued",
        },
        {
          label: "Remaining Amount",
          value: this.formatAmount(remainingAmount),
          caption: usage,
        },
        {
          label: "Expires",
          value: this.giftCard.expires || "—",
        },
      ];
    },
    // Sent in UTC, shown in the user's time zone like everywhere else
    historyDates() {
      return [
        { label: "Created", date: this.giftCard.created },
        { label: "Updated", date: this.giftCard.updated },
      ];
    },
    historyUsers() {
      return [
        { label: "Created By", user: this.giftCard.createdBy },
        { label: "Approved By", user: this.giftCard.approvedBy },
      ];
    },
  },
  created() {
    if (this.code) {
      this.cardCode = this.code.trim();
      this.getActiveGiftCard();
    }
  },
  methods: {
    async getActiveGiftCard() {
      // Enter still submits the form while a search is running
      if (!this.cardCode || this.loading) return;

      // Keep the searched code in the link, so a refresh or a shared link opens the same card
      if (this.$route.query.code !== this.cardCode) {
        this.$router.replace({ query: { ...this.$route.query, code: this.cardCode } });
      }

      this.loading = true;
      try {
        this.giftCard = await TrainplanetApi.getActiveGiftCard(this.cardCode);
      } catch (error) {
        // Don't leave the previous card on screen under the new code
        this.giftCard = null;
        await this.$store.dispatch("error", getErrorMessage(error));
      } finally {
        this.loading = false;
      }
    },
    humanize(value) {
      if (!value) return "—";
      return value
        .split(/[-_]/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    // Same format as the amounts on the Statistics and order pages, which follow the backend's plain texts
    // ("22 329 SEK", "1 497,17 SEK"): sv-SE, with two decimals only when the amount has any
    formatAmount(value) {
      const number = Number(value);
      if (value === null || value === undefined || value === "" || !Number.isFinite(number)) return "—";
      const decimals = Number.isInteger(number) ? 0 : 2;
      const text = number.toLocaleString("sv-SE", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
      return this.giftCard.currency ? `${text} ${this.giftCard.currency}` : text;
    },
  },
};
</script>

<style lang="scss" scoped>
.detail-row {
  display: grid;
  grid-template-columns: 140px minmax(0, 1fr);
  gap: 12px;
  padding: 6px 0;
  font-size: 0.875rem;
  word-break: break-word;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  &:last-child {
    border-bottom: none;
  }
}

.detail-label {
  color: #757575;
}

.pre-line {
  white-space: pre-line;
}
</style>
